import { AdsAccount } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { ColumnDef } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useContext, useMemo } from 'react';
import facebookAdsIcon from '../../assets/images/facebookAdsIcon.png';
import googleAdsIcon from '../../assets/images/googleAdsIcon.png';
import DotStatus, { DotStatusColor } from '../../components/Common/DotStatus';
import MenuButton from '../../components/Common/MenuButton';
import Tooltip from '../../components/Common/Tooltip';
import { ConnectAdsAccountButton } from '../../components/ConnectAdsAccountButton';
import { CustomDataGrid } from '../../components/CustomDataGrid/CustomDataGrid';
import { DashboardContext } from '../../contexts/DashboardContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import { resyncAdsAccount } from '../../services/AdsAccountsService';
import { track } from '../../utils/analytics';

export default function AdAccountsSettings() {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { adsAccounts } = useContext(DashboardContext);

  const relevantAdsAccounts = useMemo(() => {
    return adsAccounts?.filter((a) => a.data().status === 'active');
  }, [adsAccounts]);

  const columns: ColumnDef<QueryDocumentSnapshot<AdsAccount, AdsAccount>>[] =
    useMemo(() => {
      return [
        {
          id: 'name',
          header: 'Name',
          accessorFn: (row) => row.data().name,
          size: 250,
          cell: ({ row: { original } }) => {
            return (
              <div className="flex flex-col">
                <div className="text-gray-900">{original.data().name}</div>
                <div className="text-gray-500">
                  {original.data().externalId}
                </div>
              </div>
            );
          },
        },
        {
          id: 'provider',
          header: 'Type',
          accessorFn: (row) => row.data().provider,
          size: 250,
          cell: ({ getValue }) => {
            return (
              <div className="flex items-center gap-2">
                <img
                  src={
                    getValue() === 'google' ? googleAdsIcon : facebookAdsIcon
                  }
                  className="w-5"
                />
                <div className="text-gray-900">
                  {getValue() === 'google' ? 'Google Ads' : 'Meta Ads'}
                </div>
              </div>
            );
          },
        },
        {
          id: 'createdAt',
          header: 'Connected At',
          accessorFn: (row) => row.data().createdAt.toDate(),
          size: 200,
          cell: (info) => {
            return info.row.original
              .data()
              .createdAt.toDate()
              .toLocaleDateString();
          },
        },
        {
          id: 'currencyCode',
          header: 'Currency',
          accessorFn: (row) => row.data().currencyCode || 'USD',
          size: 200,
        },
        {
          header: 'Sync',
          accessorFn: (row) => row.data(),
          size: 200,
          cell: ({ row: { original } }) => {
            let syncMessage: string;
            let dotColor: DotStatusColor;
            let syncTitle: string;

            if (original.data().syncStatus === 'success') {
              syncMessage = `Last synced at ${original
                .data()
                .lastSyncedAt.toDate()
                .toLocaleString()}`;
              dotColor = 'green';
              syncTitle = 'Synced';
            } else if (original.data().syncStatus === 'error') {
              syncMessage = original.data().syncError ?? 'Sync failed';
              dotColor = 'red';
              syncTitle = 'Error in last Sync';
            } else {
              syncMessage = 'Not Yet Synced';
              dotColor = 'gray';
              syncTitle = 'Not Synced Yet';
            }

            return (
              <Tooltip text={syncMessage}>
                <div className="flex flex-row items-center gap-2">
                  <DotStatus color={dotColor} />
                  <span>{syncTitle}</span>
                </div>
              </Tooltip>
            );
          },
        },
        {
          header: '',
          accessorKey: 'actions',
          size: 70,
          cell: ({ row: { original } }) => (
            <MenuButton
              items={[
                {
                  title: 'Resync',
                  onClick: () => {
                    if (!selectedAccount) return;

                    track('resync_ads_account_button_click', {
                      adAccountId: original.id,
                    });

                    show('Resyncing account...', 'info');
                    resyncAdsAccount(selectedAccount.id, [original.id]).catch(
                      (e) => {
                        show('Failed to resync account', 'warn');
                        Sentry.captureException(e);
                      },
                    );
                  },
                },
                {
                  title: 'Disconnect (coming soon)',
                  destructive: true,
                  onClick: () => {},
                },
              ]}
            />
          ),
        },
      ] satisfies ColumnDef<QueryDocumentSnapshot<AdsAccount, AdsAccount>>[];
    }, [selectedAccount, show]);

  return (
    <>
      <div className="flex h-full flex-col py-8">
        <div className="pb-8 sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Ad Accounts
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Here you can manage your connected ad accounts and connect new
              ones.
            </p>
          </div>
          <ConnectAdsAccountButton />
        </div>

        <div className="relative flex-1">
          <CustomDataGrid
            logicalName="AdsAccounts"
            columns={columns}
            data={relevantAdsAccounts}
            initialState={{
              columnPinning: {
                right: ['actions'],
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
